import { AlertTriangle, Ban, PlugZap, Route } from "lucide-react";

import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import useTranslations from "@/shared/lang/useTranslations";

import type { ProviderRouteKpis } from "../../types/paymentRoutes.types";

export interface RouteKpiStripProps {
  kpis: ProviderRouteKpis;
}

/**
 * The four numbers that decide whether the payments engine can operate.
 *
 * Each accent is tied to whether the figure is a problem, not to the metric it measures: a zero in the three
 * failure counters is the healthy case and stays neutral, so a green row never has to be read twice.
 */
export function RouteKpiStrip({ kpis }: RouteKpiStripProps) {
  const { t } = useTranslations("paymentRoutes");

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <ColocaKpiCard
        icon={Route}
        accent={kpis.missing === 0 && kpis.blocked === 0 ? "success" : "primary"}
        label={t("kpi_routed_label")}
        value={`${kpis.routed}/${kpis.totalCorridors}`}
        meta={t("kpi_routed_meta", { total: kpis.totalCorridors })}
      />
      <ColocaKpiCard
        icon={AlertTriangle}
        accent={kpis.missing > 0 ? "danger" : "neutral"}
        label={t("kpi_missing_label")}
        value={kpis.missing}
        meta={kpis.missing > 0 ? t("kpi_missing_meta") : t("kpi_missing_ok")}
      />
      <ColocaKpiCard
        icon={Ban}
        accent={kpis.blocked > 0 ? "warning" : "neutral"}
        label={t("kpi_blocked_label")}
        value={kpis.blocked}
        meta={kpis.blocked > 0 ? t("kpi_blocked_meta") : t("kpi_blocked_ok")}
      />
      <ColocaKpiCard
        icon={PlugZap}
        accent={kpis.unsupported > 0 ? "warning" : "neutral"}
        label={t("kpi_unsupported_label")}
        value={kpis.unsupported}
        meta={kpis.unsupported > 0 ? t("kpi_unsupported_meta") : t("kpi_unsupported_ok")}
      />
    </div>
  );
}
